import { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { api } from '../../lib/api';
import { CheckCircle2, XCircle, Loader2, Chrome, Shield, Send } from 'lucide-react';

type AuthState = 'loading' | 'show_telegram' | 'show_google' | 'authorizing' | 'success' | 'error';

interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

interface TelegramLoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    fullName: string;
    avatarUrl?: string;
    isSuperAdmin: boolean;
  };
  isNewUser: boolean;
  expiresAt: string;
}

interface AuthProvider {
  type: string;
  displayName: string;
  displayOrder: number;
  settings: {
    botUsername?: string;
    clientId?: string;
  };
}

interface GoogleLoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    fullName: string;
    avatarUrl?: string;
    isSuperAdmin: boolean;
  };
  isNewUser: boolean;
  expiresAt: string;
}

export function ExtensionSsoPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setTokenAndFetchUser } = useAuthStore();
  const telegramContainerRef = useRef<HTMLDivElement>(null);
  
  const [authState, setAuthState] = useState<AuthState>('loading');
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);
  const [telegramProvider, setTelegramProvider] = useState<AuthProvider | null>(null);
  const [googleProvider, setGoogleProvider] = useState<AuthProvider | null>(null);
  
  const extensionId = searchParams.get('extensionId');
  const redirectUri = searchParams.get('redirect');
  const provider = searchParams.get('provider') || 'telegram';
  
  // Fetch available providers
  useEffect(() => {
    async function fetchProviders() {
      try {
        const response = await api.get<{ providers: AuthProvider[] }>('/auth-providers/public');
        
        if (provider === 'telegram') {
          const telegram = response.providers.find(p => p.type === 'telegram');
          if (telegram && telegram.settings?.botUsername) {
            setTelegramProvider(telegram);
            setAuthState('show_telegram');
          } else {
            setError('Telegram login is not configured');
            setAuthState('error');
          }
        } else if (provider === 'google') {
          const google = response.providers.find(p => p.type === 'google');
          if (google && google.settings?.clientId) {
            setGoogleProvider(google);
            setAuthState('show_google');
          } else {
            setError('Google login is not configured');
            setAuthState('error');
          }
        }
      } catch (err) {
        setError('Failed to load authentication providers');
        setAuthState('error');
      }
    }
    
    if (provider === 'telegram' || provider === 'google') {
      fetchProviders();
    } else {
      setError('Unsupported provider');
      setAuthState('error');
    }
  }, [provider]);
  
  // Initialize Telegram Login Widget
  useEffect(() => {
    if (!telegramProvider?.settings?.botUsername || !telegramContainerRef.current) return;
    if (authState !== 'show_telegram') return;
    
    // Clear container
    telegramContainerRef.current.innerHTML = '';
    
    // Create Telegram login callback
    (window as unknown as Record<string, unknown>).onTelegramAuthExtension = async (user: TelegramUser) => {
      setAuthState('authorizing');
      setError(null);
      
      try {
        // Verify with backend
        const response = await api.post<TelegramLoginResponse>('/auth-providers/telegram/verify', user);
        
        // Store token locally (for this session)
        await setTokenAndFetchUser(response.token);
        
        // Now redirect back to extension with the token
        setAuthState('success');
        
        // Build redirect URL
        let redirectUrl: string;
        if (redirectUri) {
          redirectUrl = `${redirectUri}?token=${response.token}&provider=telegram`;
        } else if (extensionId) {
          redirectUrl = `chrome-extension://${extensionId}/auth-callback.html?token=${response.token}&provider=telegram`;
        } else {
          // No redirect - show success
          return;
        }
        
        // Redirect after countdown
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 3000);
        
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Telegram login failed';
        setError(message);
        setAuthState('error');
      }
    };
    
    // Create Telegram widget script
    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-widget.js?22';
    script.setAttribute('data-telegram-login', telegramProvider.settings.botUsername);
    script.setAttribute('data-size', 'large');
    script.setAttribute('data-radius', '8');
    script.setAttribute('data-onauth', 'onTelegramAuthExtension(user)');
    script.setAttribute('data-request-access', 'write');
    script.async = true;
    
    telegramContainerRef.current.appendChild(script);
    
    return () => {
      delete (window as unknown as Record<string, unknown>).onTelegramAuthExtension;
    };
  }, [telegramProvider, authState, extensionId, redirectUri, setTokenAndFetchUser]);
  
  // Handle Google credential response
  const handleGoogleCredentialResponse = useCallback(async (response: { credential: string }) => {
    setAuthState('authorizing');
    setError(null);
    
    try {
      // Verify with backend
      const result = await api.post<GoogleLoginResponse>('/auth-providers/google/verify', {
        idToken: response.credential
      });
      
      // Store token locally
      await setTokenAndFetchUser(result.token);
      
      // Success!
      setAuthState('success');
      
      // Build redirect URL
      let redirectUrl: string | null = null;
      if (redirectUri) {
        redirectUrl = `${redirectUri}?token=${result.token}&provider=google`;
      } else if (extensionId) {
        redirectUrl = `chrome-extension://${extensionId}/auth-callback.html?token=${result.token}&provider=google`;
      }
      
      // Redirect after countdown
      if (redirectUrl) {
        setTimeout(() => {
          window.location.href = redirectUrl!;
        }, 3000);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Google login failed';
      setError(message);
      setAuthState('error');
    }
  }, [extensionId, redirectUri, setTokenAndFetchUser]);

  // Initialize Google Sign-In
  useEffect(() => {
    if (!googleProvider?.settings?.clientId) return;
    if (authState !== 'show_google') return;
    
    // Load Google Sign-In script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // @ts-expect-error Google Sign-In global
      window.google?.accounts.id.initialize({
        client_id: googleProvider.settings.clientId,
        callback: handleGoogleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      
      // Render the button
      const buttonContainer = document.getElementById('google-signin-button');
      if (buttonContainer) {
        // @ts-expect-error Google Sign-In global
        window.google?.accounts.id.renderButton(buttonContainer, {
          theme: 'filled_blue',
          size: 'large',
          shape: 'rectangular',
          text: 'signin_with',
          width: 280,
        });
      }
    };
    document.head.appendChild(script);
    
    return () => {
      script.remove();
    };
  }, [googleProvider, authState, handleGoogleCredentialResponse]);

  // Countdown for redirect
  useEffect(() => {
    if (authState === 'success' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [authState, countdown]);
  
  function handleBackToLogin() {
    navigate('/login');
  }
  
  return (
    <div className="min-h-screen bg-surface-950 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="card text-center">
          {/* Header */}
          <div className="mb-6">
            <div className="w-16 h-16 mx-auto mb-4 bg-accent-500/20 rounded-2xl flex items-center justify-center">
              {provider === 'telegram' ? (
                <Send className="w-8 h-8 text-accent-400" />
              ) : (
                <Chrome className="w-8 h-8 text-accent-400" />
              )}
            </div>
            <h1 className="text-xl font-display font-semibold text-surface-100">
              Sign in with {provider === 'telegram' ? 'Telegram' : 'SSO'}
            </h1>
            <p className="text-sm text-surface-400 mt-2">
              Authorize the extension to sync with your backoffice
            </p>
          </div>
          
          {/* States */}
          {authState === 'loading' && (
            <div className="py-8">
              <Loader2 className="w-12 h-12 mx-auto text-accent-500 animate-spin" />
              <p className="text-surface-400 mt-4">Loading authentication...</p>
            </div>
          )}
          
          {authState === 'show_telegram' && (
            <div className="py-6 space-y-4">
              <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                <Shield className="w-10 h-10 mx-auto text-surface-400 mb-3" />
                <p className="text-surface-300 mb-4">
                  Click the button below to sign in with Telegram
                </p>
              </div>
              
              {/* Telegram Widget Container */}
              <div 
                ref={telegramContainerRef}
                className="flex justify-center py-4"
              />
              
              <button
                onClick={handleBackToLogin}
                className="text-sm text-surface-400 hover:text-surface-200 transition-colors"
              >
                ← Back to login options
              </button>
            </div>
          )}
          
          {authState === 'show_google' && (
            <div className="py-6 space-y-4">
              <div className="p-4 bg-surface-800/50 rounded-lg border border-surface-700">
                <Shield className="w-10 h-10 mx-auto text-surface-400 mb-3" />
                <p className="text-surface-300 mb-4">
                  Click the button below to sign in with Google
                </p>
              </div>
              
              {/* Google Sign-In Button Container */}
              <div 
                id="google-signin-button"
                className="flex justify-center py-4"
              />
              
              <button
                onClick={handleBackToLogin}
                className="text-sm text-surface-400 hover:text-surface-200 transition-colors"
              >
                ← Back to login options
              </button>
            </div>
          )}
          
          {authState === 'authorizing' && (
            <div className="py-8">
              <Loader2 className="w-12 h-12 mx-auto text-accent-500 animate-spin" />
              <p className="text-surface-400 mt-4">
                Verifying your {provider === 'google' ? 'Google' : 'Telegram'} account...
              </p>
            </div>
          )}
          
          {authState === 'success' && (
            <div className="py-6">
              <CheckCircle2 className="w-16 h-16 mx-auto text-success-400" />
              <h2 className="text-lg font-semibold text-surface-100 mt-4">
                Authentication Successful!
              </h2>
              <p className="text-surface-400 mt-2">
                {(redirectUri || extensionId) 
                  ? `Redirecting to extension in ${countdown}...`
                  : 'You can now close this window'}
              </p>
            </div>
          )}
          
          {authState === 'error' && (
            <div className="py-6">
              <XCircle className="w-16 h-16 mx-auto text-danger-400" />
              <h2 className="text-lg font-semibold text-surface-100 mt-4">
                Authentication Failed
              </h2>
              <p className="text-danger-400 mt-2">{error}</p>
              
              <div className="flex flex-col gap-3 mt-6">
                <button
                  onClick={() => setAuthState(provider === 'google' ? 'show_google' : 'show_telegram')}
                  className="btn btn-secondary"
                >
                  Try Again
                </button>
                <button
                  onClick={handleBackToLogin}
                  className="text-sm text-surface-400 hover:text-surface-200 transition-colors"
                >
                  ← Back to login options
                </button>
              </div>
            </div>
          )}
          
          {/* Extension ID info */}
          {extensionId && (
            <div className="mt-6 pt-6 border-t border-surface-800">
              <p className="text-xs text-surface-500">
                Extension ID: <code className="text-surface-400">{extensionId}</code>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
